import { ArrowsLeftRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useOptInToV2, useRollbackFromV2 } from "~/api/queries";
import type { Document } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";

/**
 * The lane switch — one control, per document, no global flag.
 *
 * `v1` is slate's markdown Tiptap editor saving through the sculptor; `v2` is the shared
 * `@docit/editor-core` engine bound to the `document-v2.<id>` CRDT room. Both directions are
 * non-destructive: opt-in copies the v1 columns forward and FREEZES the v1 state as a
 * baseline, and rollback restores exactly that baseline while keeping the v2 columns. Nothing
 * is migrated and nothing is lost either way, which is why this is a button and not a dialog.
 *
 * Optimistic is deliberately NOT the design: the mutation invalidates the document query so
 * `editorLane` comes back from the server. A client-only lane would disagree with the server
 * the moment a rollback raced a peer's opt-in.
 */
export function EditorLaneSwitch({
  document,
  canUpdate,
}: {
  document: Document;
  canUpdate: boolean;
}) {
  const { t } = useTranslation();
  const optIn = useOptInToV2();
  const rollback = useRollbackFromV2();

  // The server gates this on resource `Update`; showing it to a reader would only produce a 403.
  if (!canUpdate) {
    return null;
  }

  const onV2 = document.editorLane === "v2";
  const busy = optIn.isPending || rollback.isPending;

  const run = () => {
    if (busy) {
      return;
    }
    const mutation = onV2 ? rollback : optIn;
    mutation.mutate(document.id, {
      onSuccess: () =>
        toast.success(onV2 ? t("Back in the old editor") : t("Opened in the new editor")),
      onError: (error) =>
        toast.error(error instanceof Error ? error.message : t("Something went wrong")),
    });
  };

  return (
    <details className="border-t border-divider pt-3 text-small text-text-tertiary">
      <summary className="w-fit cursor-pointer rounded-sm hover:text-text">
        {t("Editor options")}
      </summary>
      <div className="mt-2 flex flex-col gap-1 rounded-sm bg-bg-secondary px-3 py-2">
        <div className="flex items-center gap-2">
          <ArrowsLeftRight size={16} aria-hidden className="text-text-tertiary" />
          <Text variant="small" className="font-medium text-text">
            {onV2 ? t("New editor") : t("Old editor")}
          </Text>
          <Button variant="ghost" disabled={busy} onClick={run} className="ms-auto">
            {onV2 ? t("Keep in old editor") : t("Open in new editor")}
          </Button>
        </div>
        <Text variant="small" className="text-text-tertiary">
          {t(
            "The new editor keeps everything provisional until you commit it. Switching back is instant and loses nothing.",
          )}
        </Text>
      </div>
    </details>
  );
}
